<template>
  <section class="md:my-28 mt-3 grid gap-y-5">
    <div id="courses-header" class="flex items-center justify-between mb-5 px-3 2xl:px-0">
      <h3>{{ $t("course.title") }}</h3>
      <Button
        icon="pi pi-plus"
        :label="$t('course.create')"
        class="btn-primary p-button-sm"
        @click="go.redirectToPage('formCourse')"
        v-if="user.role == 'TEACHER'"
      />
    </div>
    <div
      id="tabs"
      class="overflow-auto"
      v-motion
      :initial="initialMotion"
      :enter="enterMotion"
    >
      <TabView
        class="custom-tabs"
        @tab-change="changeTab($event)"
        :scrollable="true"
        :activeIndex="active"
      >
        <TabPanel v-for="(tab, i) in tabs" :key="i">
          <template #header>
            <div class="mx-8 text-center flex gap-2">
              <span>{{ tab.title }}</span>
            </div>
          </template>

          <div
            class="col-span-12 mb-8"
            v-motion
            :initial="initialMotion"
            :enter="enterMotion"
          >
            <div class="grid grid-cols-12">
              <div class="col-span-12 md:col-span-6">
                <h5 class="text-h-primary-900">{{ tab.title }}</h5>
                <span class="p-input-icon-right w-full mt-4">
                  <InputText
                    type="text"
                    v-model="params.name"
                    :placeholder="$t('course.search')"
                    class="p-inputtext-sm"
                    @keyup.prevent="filterCourseByName"
                  />
                  <i class="pi pi-search" />
                </span>
              </div>
            </div>
          </div>

          <Loader v-if="loading" :loader="loading" />

          <EmptyData
            v-else-if="!courses || courses.length == 0"
            :data="!courses || courses.length == 0"
            :label="emptyState.description"
            :image="emptyState.image"
          />

          <div v-else class="grid grid-cols-12 gap-3">
            <template v-for="(course, i) in tab.content" :key="i">
              <CardCourseComponent
                :course="course"
                @onSeeMore="seeMore($event)"
                @onPublish="confirmModal($event, '')"
                @onSendToApprove="confirmModal($event, 'sendToApprove')"
                @onApprove="
                  course.type === 'ADAPTATIVE' || course.type === 'INTERACTIVE'
                    ? confirmModal($event, 'toApprove')
                    : confirmModal($event, 'toApproveTraditional')
                "
                @onReprove="dialogModal"
                @onEdit="
                  go.redirectToPage('chapters', {
                    id: course.id,
                    type:
                      course.type === 'INTERACTIVE' ? course.type : undefined,
                    h5pId: course.h5p?.h5pId ?? undefined,
                  })
                "
              />
            </template>
          </div>
          <div
            v-if="params?.total! > params?.size"
            class="flex justify-end items-center col-span-12 mt-10"
          >
            <Paginator
              :rows="params?.size"
              :totalRecords="params?.total"
              :rowsPerPageOptions="[12, 20, 30]"
              :first="params.page * params.size + 1"
              @page="paginate"
            ></Paginator>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <ConfirmDialog :style="{ width: '80vw', maxWidth: '700px' }">
      <template #message="slotProps">
        <div class="flex flex-col">
          <p class="mb-5">{{ slotProps.message.message }}</p>
          <div
            v-if="!course.adaptive && user.role == 'ADMINISTRATOR'"
            class="flex items-center gap-5 w-full"
          >
            <div v-for="(item, i) in radios" :key="i">
              <div class="flex items-center gap-3">
                <div id="btn-radio">
                  <RadioButton
                    id="radio-listed"
                    v-model="courseType"
                    :inputId="item.value"
                    :value="item.value"
                    :disabled="item.disabled"
                    name="courseType"
                    class="custom-radio"
                  />
                  <label
                    :for="item.value"
                    :class="`ml-2 cursor-pointer ${
                      item.disabled ? 'text-gray-400' : ''
                    }`"
                  >
                    {{ item.name }}
                  </label>
                </div>
                <span
                  v-tooltip.bottom="
                    `
                        ${
                          item.value == 'LISTED'
                            ? $t('course.form.types.listed')
                            : $t('course.form.types.nonListed')
                        }`
                  "
                >
                  <v-icon name="md-info-outlined" class="text-h-primary-500" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ConfirmDialog>

    <Dialog
      v-model:visible="dialogModals"
      :draggable="false"
      :style="{ width: '80vw', maxWidth: '700px' }"
      :modal="true"
    >
      <template #header>
        <h5>
          {{ $t("labels.generic", { pt: "Reprovar", en: "Reprove" }) }}
          {{ course.name }}
        </h5>
      </template>
      <div class="space-y-4">
        <p>
          {{
            $t("labels.generic", {
              pt: "Informe abaixo o motivo da reprovação.",
              en: "Please provide the reason for disapproval below.",
            })
          }}
        </p>
        <Textarea
          id="reason"
          type="text"
          name="reason"
          v-model="reason"
          rows="5"
          cols="30"
        />
      </div>
      <template #footer>
        <Button class="btn-outline" @click="dialogModals = false">
          {{
            $t("labels.generic", {
              pt: "Cancelar",
              en: "Cancel",
            })
          }}
        </Button>
        <Button
          class="btn-primary"
          @click="
            toApprove(course, false);
            dialogModals = false;
          "
        >
          {{
            $t("labels.generic", {
              pt: "Reprovar",
              en: "To reprove",
            })
          }}
        </Button>
      </template>
    </Dialog>
  </section>
</template>

<script lang="ts">
import { Course, Status } from "@/models/courses.model";
import {
  MotionEnter,
  MotionInitial,
  MotionTransition,
} from "@/models/motion.model";
import { QueryParams } from "@/models/query-params.model";
import { CoursesGenerics } from "@/utils/courses.util";
import { GoToRoute } from "@/utils/go-to.util";
import { ModalUtils } from "@/utils/modals.utils";
import { getUserFromStorage } from "@/utils/user-store.util";
import { defineComponent } from "vue";
import { CourseService } from "./../course.service";

export default defineComponent({
  name: "dashboardTeacher",
  computed: {
    service(): CourseService {
      return new CourseService();
    },
    courseGenerics(): CoursesGenerics {
      return new CoursesGenerics();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    user(): any {
      return getUserFromStorage();
    },
  },
  data() {
    return {
      initialMotion: new MotionInitial(),
      enterMotion: new MotionEnter(1, 0, 0, new MotionTransition(30)),
      courses: [] as any,
      active: 0 as number,
      filterByName: "" as string,
      emptyState: {
        image: "empty_courses.svg",
        description: this.$t("course.empty.courses"),
      } as any,
      params: { size: 12 } as QueryParams,
      tabsTeacher: [
        { title: this.$t("course.tabs.all"), content: null, type: "all" },
        {
          title: this.$t("course.tabs.published"),
          content: null,
          type: Status.PUBLISHED,
        },
        {
          title: this.$t("course.tabs.repproved"),
          content: null,
          type: Status.REPROVED,
        },
        {
          title: this.$t("course.tabs.waiting"),
          content: null,
          type: Status.WAITING,
        },
        {
          title: this.$t("course.tabs.notPublished"),
          content: null,
          type: Status.NOT_PUBLISHED,
        },
        {
          title: this.$t("course.tabs.notListed"),
          content: null,
          type: Status.NOT_LISTED,
        },
      ] as any,
      tabsAdm: [
        { title: this.$t("course.tabs.all"), content: null, type: "all" },
        {
          title: this.$t("course.tabs.waiting"),
          content: null,
          type: Status.WAITING,
        },
        {
          title: this.$t("course.tabs.published"),
          content: null,
          type: Status.PUBLISHED,
        },
        {
          title: this.$t("course.tabs.repproved"),
          content: null,
          type: Status.REPROVED,
        },
        {
          title: this.$t("course.tabs.notListed"),
          content: null,
          type: Status.NOT_LISTED,
        },
      ] as any,
      tabs: [] as any,
      dialogText: {} as ModalUtils,
      reason: "" as string,
      course: {} as Course,
      dialogModals: false as boolean,
      radios: [
        {
          name: this.$t("labels.generic", {
            pt: "Curso listado",
            en: "Listed",
          }),
          value: "LISTED",
          disabled: false,
        },
        {
          name: this.$t("labels.generic", {
            pt: "Curso não-listado",
            en: "Not-listed",
          }),
          value: "NOT_LISTED",
          disabled: false,
        },
      ] as any[],
      courseType: null as any,
      debounceTimer: null as any,
      loading: true as boolean,
    };
  },
  mounted() {
    this.coursesPaginated();
    if (this.user.role == "TEACHER") this.tabs = this.tabsTeacher;
    else this.tabs = this.tabsAdm;
  },
  watch: {
    "params.name"(val) {
      if (!val) this.resetPage();
    },
    loading(val) {
      this.loading = val;
    },
  },
  methods: {
    paginate(event: { page: number; rows: number }): void {
      this.loading = true;
      this.params.page = event.page;
      this.params.size = event.rows;
      this.setPage();
    },
    setPage(): void {
      this.loading = true;

      this.service.courses.subscribe({
        next: (response: any) => {
          this.params.total = response.total;
          this.tabs.forEach((tab: any) => {
            tab.content = response.content;
            this.courses = tab.content;
          });
          setTimeout(() => (this.loading = false), 500);
        },
      });
      this.service.getCourses(this.params);
    },
    coursesPaginated(statusQuery?: string): void {
      this.params.status = statusQuery;
      this.params.page = 0;
      this.setPage();
    },
    changeTab(event): void {
      const index = event.index;
      this.active = index;
      delete this.params.name;
      if (index === 0) this.coursesPaginated();
      else this.coursesPaginated(this.tabs[index].type);
    },
    filterCourseByName(): void {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.resetPage(), 500);
    },
    seeMore(event: any) {
      this.$confirm.require({
        message: event.disapprovalReason,
        header: `Motivo da reprovação`,
        rejectClass: "invisible hidden",
        acceptClass: "invisible hidden",
      });
    },
    sendToApprove(course: any) {
      this.courseGenerics.courses.subscribe({
        next: () => (course.status = Status.WAITING),
      });
      this.courseGenerics.sendToApprove(course.id, "/api");
    },
    toApprove(course: Course, value?: boolean) {
      let data = {
        status: value ? Status.PUBLISHED : Status.REPROVED,
        reason: this.reason ? this.reason : null,
      };

      this.courseGenerics.courses.subscribe({
        next: () => (course.status = data.status),
      });
      this.courseGenerics.approve(course.id, data, "/api");
    },
    toApproveInteractive(course: any, value?: boolean) {
      let data = {
        status: value ? Status.PUBLISHED : Status.REPROVED,
        reason: this.reason ? this.reason : null,
      };

      this.courseGenerics.courses.subscribe({
        next: () => (course.status = data.status),
      });
      this.courseGenerics.approve(course.id, data, "/api");
    },
    toApproveTraditional(course: any): void {
      let data = {
        status:
          this.courseType === "NOT_LISTED"
            ? Status.NOT_LISTED
            : Status.PUBLISHED,
        reason: this.reason ? this.reason : null,
      };

      this.courseGenerics.courses.subscribe({
        next: (e) => (course.status = e.status),
      });
      this.courseGenerics.approve(course.id, data, "/api");
    },
    confirmModal(event: any, type: string): void {
      this.course = event;
      this.courseType = this.radios[0].value;
      let modalText = new ModalUtils(event.name, type);
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: modalText.getModalsText(type).message.pt,
          en: modalText.getModalsText(type).message.en,
        }),
        header: modalText.getModalsText(type).header,
        acceptLabel: modalText.getModalsText(type).acceptLabel,
        rejectLabel: modalText.getModalsText(type).rejectLabel,
        rejectClass: modalText.getModalsText(type).rejectClass,
        acceptClass: modalText.getModalsText(type).acceptClass,
        accept: () => {
          if (type === "toApprove") {
            this.toApprove(event, true);
          } else if (type === "sendToApprove") {
            this.sendToApprove(event);
          } else if (type === "toApproveTraditional") {
            this.toApproveTraditional(event);
          }
        },
      });
    },
    dialogModal(event: any, type: string): void {
      this.course = event;
      this.dialogModals = true;
      this.dialogText = new ModalUtils(event.name, type);
    },
    resetPage(): void {
      this.params.page = 0;
      this.setPage();
    },
  },
});
</script>
